import React, { useMemo, useState } from "react";
import { Button, Checkbox, Input, Select, Tabs } from "antd";

import { useListMasterAccountLocationsQuery } from "@/features/accounts/accountsApi";
import {
  DetailField,
  DetailGrid,
  DetailSection,
} from "@/features/accounts/components/ClientDetailsFields";
import {
  useGetLocationZonesQuery,
  useLazyListMusicServiceQuery,
  usePreviewMusicServiceMutation,
  useRestoreMusicServiceMutation,
  useSuspendMusicServiceMutation,
} from "@/features/devices/devicesApi";
import {
  RESTORE_CONFIRM_PHRASE,
  SCOPE_TYPE_OPTIONS,
  SUSPEND_CONFIRM_PHRASE,
  defaultScopeTypeFromContext,
  describeAffectedScope,
  friendlyName,
  shouldShowLayoutScopeWarning,
  type MusicServiceContext,
  type MusicServiceScopeType,
} from "@/features/devices/musicServiceApi";
import styles from "./MusicServiceSuspensionModal.module.scss";

type SuspensionItem = {
  suspensionId?: string;
  scopeType?: string;
  status?: string;
  locationIds?: string[];
  zoneIds?: string[];
  createdAt?: string;
  createdByEmail?: string;
};

function asItems(payload: unknown): SuspensionItem[] {
  if (!payload || typeof payload !== "object") return [];
  const items = (payload as { items?: unknown }).items;
  if (!Array.isArray(items)) return [];
  return items.filter((row) => row && typeof row === "object") as SuspensionItem[];
}

function previewSummary(payload: unknown): { count: number; notice: string; lines: string[] } {
  if (!payload || typeof payload !== "object") {
    return { count: 0, notice: "Preview unavailable.", lines: [] };
  }
  const row = payload as Record<string, unknown>;
  const count = Number(row.affectedDeviceCount || 0);
  const notice =
    typeof row.notice === "string"
      ? row.notice
      : "Preview only. Music does not stop from this action.";
  const devices = Array.isArray(row.affectedDevices) ? row.affectedDevices : [];
  const lines = devices.slice(0, 8).map((device, index) => {
    if (!device || typeof device !== "object") return `Device ${index + 1}`;
    const d = device as Record<string, unknown>;
    const loc = typeof d.locationName === "string" ? d.locationName : "";
    const zone = typeof d.zoneName === "string" ? d.zoneName : "";
    const place = [loc, zone].filter(Boolean).join(" · ");
    return place || `Device ${index + 1}`;
  });
  return { count, notice, lines };
}

const CONFIRM_PLACEHOLDER = "Enter confirmation phrase";

const MusicServiceSuspensionPanel: React.FC<{
  context?: MusicServiceContext | null;
  onClose?: () => void;
  onEntitlementChanged?: () => void | Promise<unknown>;
}> = ({ context = null, onClose, onEntitlementChanged }) => {
  const defaultScope = defaultScopeTypeFromContext(context);
  const masterAccountId = context?.masterAccountId || "";
  const [tab, setTab] = useState("suspend");
  const [scopeType, setScopeType] = useState<MusicServiceScopeType>(defaultScope);
  const [locationId, setLocationId] = useState(context?.locationId || "");
  const [zoneId, setZoneId] = useState(context?.zoneId || "");
  const [grace, setGrace] = useState(false);
  const [phrase, setPhrase] = useState("");
  const [restoreId, setRestoreId] = useState("");
  const [restorePhrase, setRestorePhrase] = useState("");
  const [previewPayload, setPreviewPayload] = useState<unknown>(null);
  const [historyPayload, setHistoryPayload] = useState<unknown>(null);
  const [actionMessage, setActionMessage] = useState("");
  const [actionOk, setActionOk] = useState(false);

  const [preview, previewState] = usePreviewMusicServiceMutation();
  const [suspend, suspendState] = useSuspendMusicServiceMutation();
  const [restore, restoreState] = useRestoreMusicServiceMutation();
  const [list, listState] = useLazyListMusicServiceQuery();
  const busy =
    previewState.isLoading ||
    suspendState.isLoading ||
    restoreState.isLoading ||
    listState.isLoading;

  const { data: locationsData } = useListMasterAccountLocationsQuery(masterAccountId, {
    skip: !masterAccountId,
  });
  const locationForZones = locationId || context?.locationId || "";
  const { data: zonesData } = useGetLocationZonesQuery(locationForZones, {
    skip: !locationForZones,
  });

  const locationOptions = useMemo(() => {
    const rows = new Map<string, string>();
    if (context?.locationId) {
      rows.set(context.locationId, friendlyName(context.locationName, "This Location"));
    }
    for (const loc of locationsData?.locations || []) {
      if (loc.locationId) {
        rows.set(loc.locationId, friendlyName(loc.locationName, "Location"));
      }
    }
    return Array.from(rows.entries()).map(([value, label]) => ({ value, label }));
  }, [context, locationsData]);

  const zoneOptions = useMemo(() => {
    const rows = new Map<string, string>();
    if (context?.zoneId) {
      rows.set(context.zoneId, friendlyName(context.zoneName, "This Zone"));
    }
    for (const zone of zonesData?.zones || []) {
      if (zone.zoneId) {
        rows.set(zone.zoneId, friendlyName(zone.name, "Zone"));
      }
    }
    return Array.from(rows.entries()).map(([value, label]) => ({ value, label }));
  }, [context, zonesData]);

  const locationIds = scopeType === "LOCATIONS" && locationId ? [locationId] : [];
  const zoneIds = scopeType === "ZONES" && zoneId ? [zoneId] : [];
  const affectedScope = useMemo(
    () =>
      describeAffectedScope({
        context,
        scopeType,
        locationIds,
        zoneIds,
      }),
    [context, scopeType, locationId, zoneId],
  );

  const applyScope = (next: MusicServiceScopeType) => {
    setScopeType(next);
    setPreviewPayload(null);
    if (next === "LOCATIONS" && !locationId && context?.locationId) {
      setLocationId(context.locationId);
    }
    if (next === "ZONES" && !zoneId && context?.zoneId) {
      setZoneId(context.zoneId);
    }
  };

  const scopeBody = () => ({
    masterAccountId,
    scopeType,
    ...(scopeType === "LOCATIONS" ? { locationIds } : {}),
    ...(scopeType === "ZONES" ? { zoneIds } : {}),
  });

  const historyItems = asItems(historyPayload);

  // After a successful action, confirmation fields are cleared so the primary
  // button stays disabled until the operator deliberately re-enters gates —
  // preventing accidental duplicate suspend/restore submissions.
  const canSuspend =
    Boolean(masterAccountId) &&
    grace &&
    phrase === SUSPEND_CONFIRM_PHRASE &&
    !busy &&
    !(actionOk && !phrase && !grace);
  const selectedRestoreItem = historyItems.find(
    (item) => item.suspensionId && item.suspensionId === restoreId,
  );
  const selectedRestoreIsActive =
    String(selectedRestoreItem?.status || "").toLowerCase() === "active";
  const canRestore =
    Boolean(masterAccountId) &&
    selectedRestoreIsActive &&
    restorePhrase === RESTORE_CONFIRM_PHRASE &&
    !busy;

  const loadHistory = async () => {
    if (!masterAccountId) return;
    setTab("restore");
    try {
      setHistoryPayload(await list(masterAccountId).unwrap());
    } catch {
      setActionMessage("Could not load suspension history.");
    }
  };
  const previewView = previewPayload ? previewSummary(previewPayload) : null;
  const showLayoutWarning = shouldShowLayoutScopeWarning({
    scopeType,
    locationIds,
    zoneIds,
  });

  const accountSection = masterAccountId ? (
    <div
      data-testid="music-service-device-context"
      data-master-account-id={masterAccountId}
      data-location-id={context?.locationId || ""}
      data-zone-id={context?.zoneId || ""}
    >
      <DetailSection
        title="Account & Location"
        icon="bi bi-building"
        data-testid="music-service-account-section"
      >
        <div className={styles.accountGrid}>
          <DetailGrid>
            <DetailField icon="bi bi-building" label="Client">
              {friendlyName(context?.masterAccountName, "—")}
            </DetailField>
            <DetailField icon="bi bi-shop" label="Store">
              {friendlyName(context?.locationName, "—")}
            </DetailField>
            <DetailField icon="bi bi-geo-alt" label="Location / Zone">
              {friendlyName(context?.zoneName, "—")}
            </DetailField>
          </DetailGrid>
        </div>
      </DetailSection>
    </div>
  ) : (
    <p className={styles.empty}>This device has no Master Account context.</p>
  );

  return (
    <div className={styles.shell} data-testid="music-service-suspension-panel">
      <div className={styles.scroll} data-testid="music-service-modal-scroll">
        <Tabs
          size="small"
          className={styles.tabs}
          activeKey={tab}
          onChange={(key) => {
            setTab(key);
            if (key === "restore" && masterAccountId && !historyPayload) {
              void loadHistory();
            }
          }}
          items={[
            { key: "suspend", label: "Suspend" },
            { key: "restore", label: "Restore / History" },
          ]}
        />

        {accountSection}

        {tab === "suspend" ? (
          <>
            <DetailSection
              title="Suspension Settings"
              icon="bi bi-sliders"
              data-testid="music-service-settings-section"
            >
              <div className={styles.columns} data-testid="music-service-scope-columns">
                <div
                  className={styles.field}
                  data-testid="music-service-scope"
                  data-value={scopeType}
                >
                  <div className={styles.label} id="music-service-scope-label">
                    Suspension scope
                  </div>
                  <Select
                    className={styles.targetSelect}
                    aria-label="Suspension scope"
                    data-testid="music-service-scope-select"
                    value={scopeType}
                    options={SCOPE_TYPE_OPTIONS}
                    onChange={(value) => applyScope(value as MusicServiceScopeType)}
                  />
                </div>
                <div className={styles.field}>
                  {scopeType === "ZONES" ? (
                    <>
                      <div className={styles.label}>Zone</div>
                      <Select
                        className={styles.targetSelect}
                        aria-label="Zone"
                        data-testid="music-service-zone-select"
                        value={zoneId || undefined}
                        options={zoneOptions}
                        onChange={(value) => setZoneId(value)}
                      />
                    </>
                  ) : null}
                  {scopeType === "LOCATIONS" ? (
                    <>
                      <div className={styles.label}>Location</div>
                      <Select
                        className={styles.targetSelect}
                        aria-label="Location"
                        data-testid="music-service-location-select"
                        value={locationId || undefined}
                        options={locationOptions}
                        onChange={(value) => setLocationId(value)}
                      />
                    </>
                  ) : null}
                  {scopeType === "ALL_LOCATIONS" ? (
                    <>
                      <div className={styles.label}>Affected scope</div>
                      <div
                        className={styles.scopeSummary}
                        data-testid="music-service-all-locations-summary"
                      >
                        {affectedScope}
                      </div>
                    </>
                  ) : null}
                </div>
              </div>

              <input
                type="hidden"
                data-testid="music-service-location-ids"
                value={locationId}
                readOnly
              />
              <input
                type="hidden"
                data-testid="music-service-zone-ids"
                value={zoneId}
                readOnly
              />

              <div className={styles.pills}>
                <Button
                  className={styles.pill}
                  disabled={busy || !masterAccountId}
                  onClick={async () => {
                    try {
                      setPreviewPayload(await preview(scopeBody()).unwrap());
                      setActionMessage("");
                    } catch {
                      setActionMessage("Could not preview affected devices.");
                    }
                  }}
                >
                  Preview devices
                </Button>
              </div>

              {showLayoutWarning ? (
                <div className={styles.safety} data-testid="music-service-scope-warning">
                  {affectedScope}
                </div>
              ) : null}

              {previewView ? (
                <div className={styles.preview} data-testid="music-service-preview">
                  <div className={styles.previewTitle}>{affectedScope}</div>
                  <div>
                    {previewView.count} {previewView.count === 1 ? "device" : "devices"} in this
                    scope
                  </div>
                  {previewView.lines.map((line) => (
                    <div key={line}>{line}</div>
                  ))}
                  <div>{previewView.notice}</div>
                </div>
              ) : null}

              <Checkbox
                className={styles.graceCheck}
                checked={grace}
                onChange={(event) => setGrace(event.target.checked)}
                data-testid="music-service-grace"
              >
                14-day payment grace period has expired
              </Checkbox>
            </DetailSection>

            <DetailSection
              title="Confirmation"
              icon="bi bi-shield-check"
              data-testid="music-service-confirm-section"
            >
              <div
                className={styles.confirmScope}
                data-testid="music-service-affected-scope"
                data-location-ids={locationIds.join(",")}
                data-zone-ids={zoneIds.join(",")}
                data-master-account-id={masterAccountId}
              >
                {affectedScope}
              </div>
              <p
                className={styles.confirmHelp}
                data-testid="music-service-confirm-help"
              >
                To suspend music service, type{" "}
                <strong className={styles.confirmPhrase}>{SUSPEND_CONFIRM_PHRASE}</strong>{" "}
                below.
              </p>
              <Input
                className={styles.confirmInput}
                placeholder={CONFIRM_PLACEHOLDER}
                aria-label="Suspend confirmation"
                value={phrase}
                onChange={(event) => setPhrase(event.target.value)}
              />
            </DetailSection>
          </>
        ) : (
          <>
            <DetailSection
              title="Restore / History"
              icon="bi bi-clock-history"
              data-testid="music-service-history-section"
            >
              {historyItems.length === 0 ? (
                <div className={styles.empty} data-testid="music-service-history-empty">
                  {listState.isLoading ? "Loading history…" : "No suspensions for this account."}
                </div>
              ) : (
                <div className={styles.historyList} data-testid="music-service-history">
                  {historyItems.map((item) => {
                    const id = item.suspensionId || "";
                    const status = String(item.status || "unknown").toLowerCase();
                    const isActive = status === "active";
                    const selected = isActive && restoreId === id;
                    const scopeLabel =
                      item.scopeType === "ALL_LOCATIONS"
                        ? "All Locations"
                        : item.scopeType === "LOCATIONS"
                          ? "Selected Location"
                          : "Selected Zone";
                    const body = (
                      <>
                        <div>
                          <span className={styles.historyStatus}>{item.status || "unknown"}</span>
                          {" · "}
                          {scopeLabel}
                        </div>
                        <div>
                          {item.createdAt ? new Date(item.createdAt).toLocaleString() : ""}
                          {item.createdByEmail ? ` · ${item.createdByEmail}` : ""}
                        </div>
                      </>
                    );
                    if (!isActive) {
                      // Historical / already-restored rows are read-only: never selectable.
                      return (
                        <div
                          key={id || scopeLabel}
                          className={`${styles.historyRow} ${styles.historyRowReadonly}`}
                          data-testid="music-service-history-readonly"
                          data-status={status}
                          data-suspension-id={id}
                          aria-disabled="true"
                        >
                          {body}
                        </div>
                      );
                    }
                    return (
                      <button
                        type="button"
                        key={id || scopeLabel}
                        className={`${styles.historyRow} ${
                          selected ? styles.historyRowSelected : ""
                        }`}
                        data-testid="music-service-history-active"
                        data-status="active"
                        data-suspension-id={id}
                        aria-pressed={selected}
                        onClick={() => {
                          setRestoreId(id);
                          setActionOk(false);
                          setActionMessage("");
                        }}
                      >
                        {body}
                      </button>
                    );
                  })}
                </div>
              )}
            </DetailSection>

            <DetailSection
              title="Confirmation"
              icon="bi bi-shield-check"
              data-testid="music-service-restore-confirm-section"
            >
              <p
                className={styles.confirmHelp}
                data-testid="music-service-restore-confirm-help"
              >
                To restore music service, type{" "}
                <strong className={styles.confirmPhrase}>{RESTORE_CONFIRM_PHRASE}</strong>{" "}
                below.
              </p>
              <Input
                className={styles.confirmInput}
                placeholder={CONFIRM_PLACEHOLDER}
                aria-label="Restore confirmation"
                value={restorePhrase}
                onChange={(event) => setRestorePhrase(event.target.value)}
              />
              <input
                type="hidden"
                data-testid="music-service-restore-id"
                value={restoreId}
                readOnly
              />
            </DetailSection>
          </>
        )}
      </div>

      <div className={styles.footer} data-testid="music-service-modal-footer">
        {actionMessage ? (
          <div
            className={actionOk ? styles.resultOk : styles.resultError}
            data-testid="music-service-result"
            data-ok={actionOk ? "true" : "false"}
            role="status"
          >
            {actionMessage}
          </div>
        ) : null}
        <div className={styles.footerActions}>
          <Button onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          {tab === "suspend" ? (
            <Button
              type="primary"
              danger
              disabled={!canSuspend}
              onClick={async () => {
                try {
                  const result = await suspend({
                    ...scopeBody(),
                    gracePeriodAttested: true,
                    confirmPhrase: SUSPEND_CONFIRM_PHRASE,
                  }).unwrap();
                  setActionOk(true);
                  setActionMessage(
                    typeof (result as { message?: string })?.message === "string"
                      ? String((result as { message?: string }).message)
                      : "Suspension recorded. Devices were not notified.",
                  );
                  setPhrase("");
                  setGrace(false);
                  if (masterAccountId) {
                    try {
                      setHistoryPayload(await list(masterAccountId).unwrap());
                    } catch {
                      /* history refresh is best-effort */
                    }
                  }
                  await onEntitlementChanged?.();
                } catch {
                  setActionOk(false);
                  setActionMessage("Could not suspend music service.");
                }
              }}
            >
              Suspend service
            </Button>
          ) : (
            <Button
              type="primary"
              disabled={!canRestore}
              onClick={async () => {
                try {
                  const result = await restore({
                    masterAccountId,
                    suspensionId: restoreId,
                    confirmPhrase: RESTORE_CONFIRM_PHRASE,
                  }).unwrap();
                  setActionOk(true);
                  setActionMessage(
                    typeof (result as { message?: string })?.message === "string"
                      ? String((result as { message?: string }).message)
                      : "Restore recorded. Devices were not notified.",
                  );
                  setRestorePhrase("");
                  setRestoreId("");
                  if (masterAccountId) {
                    setHistoryPayload(await list(masterAccountId).unwrap());
                  }
                  await onEntitlementChanged?.();
                } catch {
                  setActionOk(false);
                  setActionMessage("Could not restore music service.");
                }
              }}
            >
              Restore
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default MusicServiceSuspensionPanel;
